/**
 * W5 helper: suggest params from trailing MRR/signups series (pure fn; UI can call later).
 */

function round(n, d = 2) {
  const f = 10 ** d;
  return Math.round(n * f) / f;
}

function roundMaybe(n, d = 2) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return round(n, d);
}

/**
 * @param {Array<{ mrr?: number, logos?: number, signups?: number }>} series — oldest first
 */
export function calibrateFromSeries(series) {
  if (!series?.length || series.length < 2) {
    return {
      ok: false,
      message: 'Need at least 2 months of trailing data.',
      suggestions: null,
    };
  }

  const mrrGrowth = [];
  const logoGrowth = [];
  const signups = [];

  for (let i = 1; i < series.length; i += 1) {
    const prev = series[i - 1];
    const curr = series[i];
    if (prev.mrr > 0 && curr.mrr != null) {
      mrrGrowth.push((curr.mrr - prev.mrr) / prev.mrr);
    }
    if (prev.logos > 0 && curr.logos != null) {
      logoGrowth.push((curr.logos - prev.logos) / prev.logos);
    }
    if (curr.signups != null) signups.push(curr.signups);
  }

  const avgMrrGrowth = mrrGrowth.length
    ? mrrGrowth.reduce((s, v) => s + v, 0) / mrrGrowth.length
    : 0;

  // Logo growth alone cannot imply a 5% churn prior. Suggest contraction
  // only when we have 2+ growth points; otherwise leave the field empty.
  const lastLogoGrowth = logoGrowth[logoGrowth.length - 1];
  const impliedChurn =
    logoGrowth.length < 2 ? null : Math.max(0, -lastLogoGrowth);

  const avgSignups = signups.length
    ? signups.reduce((s, v) => s + v, 0) / signups.length
    : null;

  const last = series[series.length - 1];
  const arpu =
    last.logos > 0 && last.mrr != null && Number.isFinite(Number(last.mrr))
      ? last.mrr / last.logos
      : null;

  const expansionPctMo =
    avgMrrGrowth > 0 && logoGrowth.length
      ? Math.max(0, (avgMrrGrowth - (logoGrowth[logoGrowth.length - 1] || 0)) * 100)
      : null;

  return {
    ok: true,
    message: `Calibrated from ${series.length} months of trailing data.`,
    suggestions: {
      openingMrr: round(last.mrr ?? 0),
      openingLogos: round(last.logos ?? 0),
      arpu: roundMaybe(arpu),
      expansionPctMo: roundMaybe(expansionPctMo, 1),
      impliedMonthlyChurnPct: roundMaybe(
        impliedChurn == null ? null : impliedChurn * 100,
        1
      ),
      avgMonthlySignups: avgSignups == null ? null : round(avgSignups, 0),
      avgMrrGrowthPctMo: round(avgMrrGrowth * 100, 1),
    },
  };
}

/**
 * Map churn suggestion to retention M1 estimate.
 * @param {number} monthlyChurnPct
 */
export function churnToM1Retention(monthlyChurnPct) {
  return round(1 - monthlyChurnPct / 100, 2);
}

/**
 * Suggest opening forecast parameters from live admin aggregates.
 * @param {object} params
 * @param {number|null|undefined} params.mrrCents
 * @param {number|null|undefined} params.activeSubscriptions
 * @param {Array<{ count?: number }>|null|undefined} params.signupSeries
 * @returns {{ openingMrr: number|null, logos: number|null, signupRatePerMonth: number|null }|null}
 */
export function suggestForecastParamsFromActuals({
  mrrCents,
  activeSubscriptions,
  signupSeries,
}) {
  const hasMrr = mrrCents != null && Number.isFinite(Number(mrrCents));
  const hasLogos =
    activeSubscriptions != null && Number.isFinite(Number(activeSubscriptions));
  const points = Array.isArray(signupSeries)
    ? signupSeries.filter(
        (row) => row?.count != null && Number.isFinite(Number(row.count))
      )
    : [];
  if (!hasMrr && !hasLogos && points.length === 0) return null;

  const openingMrr = hasMrr
    ? Math.round((Math.max(0, Number(mrrCents)) / 100) * 100) / 100
    : null;
  const logos = hasLogos ? Math.max(0, Number(activeSubscriptions)) : null;
  const totalSignups = points.reduce((sum, row) => sum + Number(row.count), 0);
  const signupRatePerMonth =
    points.length > 0
      ? Math.round((totalSignups / points.length) * 30 * 10) / 10
      : null;

  return {
    openingMrr,
    logos,
    signupRatePerMonth,
  };
}

function formatMonthLabel(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Build persisted syncedActuals from trailing Stripe MRR series (oldest first).
 * @param {object} params
 * @param {Array<{ asOf?: string, mrrCents?: number, activeSubscriptions?: number }>} [params.mrrSeries]
 * @param {string|null|undefined} [params.asOf] — summary as-of when series is empty
 */
export function buildSyncedActuals({ mrrSeries, asOf }) {
  const points = [...(mrrSeries || [])].sort(
    (a, b) => new Date(a.asOf).getTime() - new Date(b.asOf).getTime()
  );

  // AdminStripeSnapshot is captured daily. Forecast rows are monthly, so keep
  // the latest snapshot in each UTC month rather than treating every day as a
  // separate forecast period.
  const monthlyPoints = [];
  const pointByMonth = new Map();
  for (const point of points) {
    const date = new Date(point.asOf);
    const monthKey = Number.isNaN(date.getTime())
      ? null
      : `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    if (!monthKey) {
      monthlyPoints.push(point);
      continue;
    }
    const existingIndex = pointByMonth.get(monthKey);
    if (existingIndex == null) {
      pointByMonth.set(monthKey, monthlyPoints.length);
      monthlyPoints.push(point);
    } else {
      monthlyPoints[existingIndex] = point;
    }
  }

  const mrrRows = monthlyPoints.map((point) => {
    const date = new Date(point.asOf);
    const monthKey = Number.isNaN(date.getTime())
      ? undefined
      : `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    const hasMrr =
      point.mrrCents != null && Number.isFinite(Number(point.mrrCents));
    return {
      label: formatMonthLabel(point.asOf),
      mrr: hasMrr ? round(Number(point.mrrCents) / 100) : null,
      ...(monthKey ? { monthKey } : {}),
      ...(point.activeSubscriptions != null
        ? { logos: point.activeSubscriptions }
        : {}),
    };
  });

  const logosSeries = mrrRows
    .filter((row) => row.logos != null)
    .map(({ label, monthKey, logos }) => ({
      label,
      logos,
      ...(monthKey ? { monthKey } : {}),
    }));

  const resolvedAsOf =
    asOf || (points.length ? points[points.length - 1].asOf : null) || new Date().toISOString();

  if (!mrrRows.length) {
    return null;
  }

  return {
    asOf: resolvedAsOf,
    mrrSeries: mrrRows.map(({ label, mrr, monthKey }) => ({
      label,
      mrr,
      ...(monthKey ? { monthKey } : {}),
    })),
    ...(logosSeries.length ? { logosSeries } : {}),
  };
}
